import axios from "axios";
import { supabase } from "./supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api/v1`;

// ── Auth metric beacon ────────────────────────────────────────────────
// Fire-and-forget POST so ops can see silent refresh-retries and refresh-
// failures piling up before users complain. Never awaited by the caller,
// never allowed to throw or block the actual auth flow. Uses sendBeacon
// when available so it survives a mid-refresh navigation.
const emitAuthBeacon = (event, extra = {}) => {
  try {
    const body = JSON.stringify({
      event,
      path: (typeof window !== "undefined" && window.location?.pathname) || null,
      status_code: extra.status_code ?? null,
      note: extra.note ?? null,
    });
    const url = `${API}/support/whoami-error`;
    // sendBeacon can't attach an Authorization header — the endpoint
    // treats the caller as anonymous in that case, which is fine
    // (server still logs the event). Prefer fetch with the auth header
    // so persisted rows carry the agent identity when we can supply it.
    const token = extra.token || null;
    if (token) {
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
        keepalive: true,
      }).catch(() => {});
    } else if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) {
    // Beacon must never disrupt auth flow.
    console.debug("[api] auth beacon failed", e);
  }
};

// ── Impersonation ─────────────────────────────────────────────────────
// Stored in `sessionStorage` (not localStorage) so the impersonation
// stays in the new tab that the admin opened it from — the admin's
// original tab never impersonates. Cleared when the "Exit" banner is
// clicked or the token 401s.
export const IMPERSONATION_KEY = "ikey_impersonation_token";
export const IMPERSONATION_META_KEY = "ikey_impersonation_meta";

export const getImpersonationToken = () => {
  try { return sessionStorage.getItem(IMPERSONATION_KEY); } catch { return null; }
};
export const getImpersonationMeta = () => {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_META_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
export const clearImpersonation = () => {
  try {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    sessionStorage.removeItem(IMPERSONATION_META_KEY);
  } catch (e) { console.debug("clearImpersonation ignored", e); }
};

// Agent-facing axios: injects the impersonation token when present,
// otherwise the real Supabase session. 401 redirects to /login unless
// impersonating, in which case we exit impersonation and reload so the
// admin's real session takes over.
export const api = axios.create({ baseURL: API });

api.interceptors.request.use(async (cfg) => {
  const imp = getImpersonationToken();
  if (imp) {
    cfg.headers.Authorization = `Bearer ${imp}`;
    return cfg;
  }
  // Try the cached session first; if it's expired or missing but a
  // refresh token exists, ask Supabase to refresh BEFORE the request
  // goes out. Prevents the "logged in but first request 401s because
  // the token expired 4 seconds ago" race that we were seeing right
  // after tab-refocus on long sessions.
  let { data: { session } } = await supabase.auth.getSession();
  if (session?.expires_at) {
    const expMs = session.expires_at * 1000;
    // If token expires in <30s, proactively refresh so this request
    // doesn't ship a stale one.
    if (Date.now() > expMs - 30_000) {
      try {
        const { data } = await supabase.auth.refreshSession();
        if (data?.session) session = data.session;
      } catch (e) { console.debug("[api] proactive refresh failed", e); }
    }
  }
  if (session?.access_token) cfg.headers.Authorization = `Bearer ${session.access_token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err?.response?.status === 401) {
      if (getImpersonationToken()) {
        // Impersonation token expired or was invalidated — drop it and
        // let the admin's real Supabase session resume next request.
        clearImpersonation();
        window.location.reload();
        return Promise.reject(err);
      }
      // ONE reactive refresh attempt before nuking the session. Handles
      // the case where the interceptor sent a token Supabase considered
      // valid but the server saw as expired (clock skew, exactly-at-
      // boundary refresh race). If refresh succeeds AND the original
      // request hasn't already been retried, retry it once with the
      // fresh token; if that still 401s, sign out.
      const cfg = err.config || {};
      const reqPath = (() => { try { return new URL(cfg.url || "", API).pathname; } catch { return cfg.url || null; } })();
      if (!cfg.__isRetry) {
        try {
          const { data } = await supabase.auth.refreshSession();
          if (data?.session?.access_token) {
            cfg.__isRetry = true;
            cfg.headers = cfg.headers || {};
            cfg.headers.Authorization = `Bearer ${data.session.access_token}`;
            emitAuthBeacon("refresh_retry", {
              status_code: 401,
              note: reqPath || null,
              token: data.session.access_token,
            });
            return api.request(cfg);
          }
          // Supabase returned no session — treat as failure below.
          emitAuthBeacon("refresh_failure", { status_code: 401, note: reqPath || "no_session" });
        } catch (e) {
          console.debug("[api] 401 refresh failed", e);
          emitAuthBeacon("refresh_failure", { status_code: 401, note: (e && e.message) || "refresh_error" });
        }
      } else {
        // Second 401 after a fresh token — the refresh worked but the
        // server still rejected. Worth surfacing as its own failure.
        emitAuthBeacon("refresh_failure", { status_code: 401, note: "retry_still_401" });
      }
      await supabase.auth.signOut();
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// Client-portal axios: separate instance so the agent's Supabase session
// never leaks into portal requests, and a 401 sends the client to
// /portal/expired instead of the agent login screen.
const PORTAL_KEY = "realtor_os_portal_token";
export const portalTokenGet = () => localStorage.getItem(PORTAL_KEY);
export const portalTokenSet = (t) => localStorage.setItem(PORTAL_KEY, t);
export const portalTokenClear = () => localStorage.removeItem(PORTAL_KEY);

export const portal = axios.create({ baseURL: API });
portal.interceptors.request.use((cfg) => {
  const t = portalTokenGet();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
portal.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      portalTokenClear();
      if (!window.location.pathname.startsWith("/portal/verify")) {
        window.location.href = "/portal/expired";
      }
    }
    return Promise.reject(err);
  }
);
